import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import LoginHeader from './components/LoginHeader';
import LoginForm from './components/LoginForm';
import SocialLoginOptions from './components/SocialLoginOptions';
import SecurityFeatures from './components/SecurityFeatures';

const UserLogin = () => {
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user is already authenticated
    const isAuthenticated = localStorage.getItem('isAuthenticated');
    if (isAuthenticated === 'true') {
      navigate('/outage-dashboard');
    }

    // Set page title
    document.title = 'Sign In - ServiceWatch | Real-time Service Outage Monitoring';
  }, [navigate]);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-16">
        <div className="min-h-screen flex items-center justify-center px-4 py-12">
          <div className="w-full max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              
              {/* Left Column - Login Form */}
              <div className="w-full max-w-md mx-auto lg:mx-0">
                <div className="bg-card border border-border rounded-xl shadow-sm p-8">
                  <LoginHeader />
                  <LoginForm />
                  <div className="mt-8">
                    <SocialLoginOptions />
                  </div>
                </div>
              </div>

              {/* Right Column - Security Features & Info */}
              <div className="w-full max-w-lg mx-auto lg:mx-0">
                <div className="space-y-8">
                  
                  {/* Hero Section */}
                  <div className="text-center lg:text-left">
                    <h2 className="text-4xl font-bold text-foreground mb-4">
                      Stay Informed About Service Outages
                    </h2>
                    <p className="text-lg text-muted-foreground mb-6">
                      Join thousands of users who rely on ServiceWatch for real-time outage monitoring, 
                      instant notifications, and comprehensive service analytics.
                    </p>
                    
                    {/* Key Benefits */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                      <div className="flex items-center space-x-3 p-4 bg-success/10 rounded-lg">
                        <div className="flex items-center justify-center w-10 h-10 bg-success rounded-lg">
                          <span className="text-success-foreground font-bold text-lg">24/7</span>
                        </div>
                        <div>
                          <h3 className="font-semibold text-foreground">Live Monitoring</h3>
                          <p className="text-sm text-muted-foreground">Real-time updates</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-3 p-4 bg-primary/10 rounded-lg">
                        <div className="flex items-center justify-center w-10 h-10 bg-primary rounded-lg">
                          <span className="text-primary-foreground font-bold text-lg">500+</span>
                        </div>
                        <div>
                          <h3 className="font-semibold text-foreground">Services Tracked</h3>
                          <p className="text-sm text-muted-foreground">Global coverage</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Security Features */}
                  <SecurityFeatures />

                  {/* Recent Activity Preview */}
                  <div className="bg-card border border-border rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-foreground mb-4">
                      Recent Outage Activity
                    </h3>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-3 bg-muted/50 rounded-md">
                        <div className="flex items-center space-x-3">
                          <div className="status-indicator status-offline"></div>
                          <div>
                            <p className="text-sm font-medium text-foreground">CloudFlare CDN</p>
                            <p className="text-xs text-muted-foreground">2 minutes ago</p>
                          </div>
                        </div>
                        <span className="text-xs bg-error/10 text-error px-2 py-1 rounded">Major</span>
                      </div>
                      
                      <div className="flex items-center justify-between p-3 bg-muted/50 rounded-md">
                        <div className="flex items-center space-x-3">
                          <div className="status-indicator status-degraded"></div>
                          <div>
                            <p className="text-sm font-medium text-foreground">AWS US-East</p>
                            <p className="text-xs text-muted-foreground">15 minutes ago</p>
                          </div>
                        </div>
                        <span className="text-xs bg-warning/10 text-warning px-2 py-1 rounded">Minor</span>
                      </div>
                      
                      <div className="flex items-center justify-between p-3 bg-muted/50 rounded-md">
                        <div className="flex items-center space-x-3">
                          <div className="status-indicator status-online"></div>
                          <div>
                            <p className="text-sm font-medium text-foreground">Google Services</p>
                            <p className="text-xs text-muted-foreground">1 hour ago</p>
                          </div>
                        </div>
                        <span className="text-xs bg-success/10 text-success px-2 py-1 rounded">Resolved</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default UserLogin;